import { fetchBaseQuery, retry, createApi } from "@reduxjs/toolkit/query/react";
import { storage } from "../utils/storage";

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = getState().auth.token;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Accept", "application/json"); // add Accept header
    return headers;
  },
});

// Custom baseQuery wrapper to handle 401 errors appropriately
export const baseQueryWithAuthHandler = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Check if the response is a 401 error
  if (result.error && result.error.status === 401) {
    const { getState } = api;
    const token = getState().auth.token;
    const url = typeof args === "string" ? args : args?.url || "";

    // Don't redirect on 401 for login/forgot password endpoints - let them be handled by the component
    const isAuthEndpoint =
      url.includes("/onboard/cooperatives/login") ||
      url.includes("/onboard/investors/login") ||
      url.includes("/onboard/investors/signup") ||
      url.includes("/onboard/cooperatives/forgot_password") ||
      url.includes("/onboard/cooperatives/reset");

    // Only redirect if:
    // 1. It's NOT an auth endpoint (auth 401 should show error message)
    // 2. AND we had a token (session expired, not a failed auth attempt)
    if (!isAuthEndpoint && token) {
      // Clear stored data
      storage["localStorage"].remove("coop_details_profile");
      storage["localStorage"].remove("company_id");
      storage["localStorage"].remove("user_details_profile");
      storage["localStorage"].remove("user_type");
      storage["cookieStorage"].remove("access_token");

      // Redirect to login page
      // eslint-disable-next-line no-restricted-globals
      location.assign("/login");
    }
  }

  // Bail out of retries for client errors (e.g. 403 exited membership) so UI
  // can show the API message immediately instead of a silent loading delay.
  const status = result.error?.status;
  if (typeof status === "number" && status >= 400 && status < 500) {
    retry.fail(result.error);
  }

  return result;
};

export const baseQueryWithRetry = retry(baseQueryWithAuthHandler, {
  maxRetries: 1,
});

export const API = createApi({
  reducerPath: "Api", // optional
  baseQuery: baseQueryWithRetry,
  endpoints: (builder) => ({}),
  tagTypes: [
    "Branch",
    "Prefix",
    "RegFee",
    "BankInfo",
    "Member",
    "PendingMember",
    "Staff",
    "Staff-list",
    "AdminEminute",
    "AdminEvent",
    "AdminAttendance",
    "GenerateMemberId",
    "AutoBirthday",
    "EventSettings",
    "CommGroupMgt",
    "contribution-records",
    "contribution-category",
    "StaffDashboard",
    "CommunicationBalances",
    "CommGroupMessage",
    "member-contributions-history",
    "member-contributions-history-v2",
    "member-contributions-summary",
    "member-contributions-history-by-category-id",
    "member-opening-balance-corrections",
    "OpeningBalanceCorrectionAudits",
    "MemberEvent",
    "pending-loans-list",
    "approved-loans-list",
    "declined-loans-list",
    "disbursed-loans-list",
    "loan-types-list",
    "selectable-loan-types-list",
    "guarantors-list",
    "upcoming-loan-repayment",
    "due-loan-repayment",
    "overdue-loan-repayment",
    "loan-request-breakdown-by-id",
    "loan-request-details",
    "migrated-loans-list",
    "migrated-loan-detail",
    "company-loan-types-list",
    "single-staff",
    "member-pending-loans-list",
    "member-approved-loans-list",
    "member-declined-loans-list",
    "member-disbursed-loans-list",
    "member-upcoming-repayment-list",
    "member-due-repayment-list",
    "member-overdue-repayment-list",
    "member-transaction-history",
    "member-loan-summary",
    "member-guarantor-requests",
    "member-guarantor-requests-summary",
    "member-dashboard",
    "Notification",
    "Activity",
    "MemberBankAccount",
    "TodayBirthdays",
    "ReceivedWishes",
    "BirthdaySettings",
    "BirthdayVisibility",
    "BirthdayLogs",
    "Fines",
    "MemberAttendance",
    "staff-transaction-history",
    "SmsHistory",
    "member-communication-messages",
    "MemberEminute",
    "MemberSlotCapacity",
    "branch-wallet-balance",
    "branch-wallet-ledger",
    "branch-wallet-withdrawals",
    "AuditLog",
    "bank-transfer-payments",
    "member-bank-transfer-payments",
    "online-pending-payments",
    "member-online-pending-payments",
    "InvestorProfile",
    "InvestorKyc",
    "StaffInvestorKyc",
    "InvestorPortalSettings",
    "StaffOpportunities",
    "StaffInvestmentRequests",
    "StaffInvestmentDashboard",
    "StaffInvestmentParticipants",
    "InvestorOpportunities",
    "InvestorInvestmentRequests",
    "InvestorSupport",
    "InvestorPaymentRails",
    "SupportSettings",
    "MemberReferralSettings",
    "StaffReferralInvites",
    "MarketplaceCategories",
    "MarketplaceProducts",
    "MarketplaceProduct",
    "MarketplaceOrders",
    "MarketplaceOrder",
    "StaffMarketplaceDashboard",
    "StaffMarketplaceCategories",
    "StaffMarketplaceProducts",
    "StaffMarketplaceOrders",
    "RealEstateRequests",
    "RealEstateRequest",
    "RealEstateProperties",
    "RealEstateProperty",
    "StaffRealEstateSummary",
    "StaffRealEstateRequests",
    "StaffRealEstateRequest",
    "StaffRealEstateInspections",
    "StaffWebsiteTestimonials",
    "CompanyRoles",
    "member-exit-requests",
    "MemberExit",
    "MemberReferral",
  ],
});

export const ApiPrefixes = {
  staffManagement: "cooperatives/staff_managements",
  memberManagement: "/cooperatives/member_managements",
  adminSettings: "/cooperatives/settings",
  adminSelectables: "/cooperatives/selectables",
  adminBranchSettings: "/cooperatives/settings/branches",
  adminBankSettings: "/cooperatives/settings/banks",
  adminCompanyPrefixesSettings: "/cooperatives/settings/branches/prefixes",
  adminRegFeesSettings: "/cooperatives/settings/registration-fees",
  adminPortalSettings: "/cooperatives/settings/portal",
  adminSupportSettings: "/cooperatives/settings/support",
  generalSelectables: "/generals",
  contribution: "cooperatives/member_contributions",
  loanManagement: "cooperatives/loans",
  membersLoan: "members/loans",
  companyBank: "cooperatives/settings/banks",
  adminTransactionHistories: "cooperatives/transaction_histories",
  branchWallet: "cooperatives/branch_wallet",
};
